import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import find from 'lodash/find';

import { OrderListsState } from '../../store/orderlist/state';
import { AppState } from '../../store/appState';
import { AppDispatch } from '../../store';
import { createSelectionlist } from '../../store/orderlist/actions';
import { Orderlist } from '../../../models/OrderList';

type UseOrderLists = {
    /**
     * The currently active order list.
     * In most cases this is the list which has been modified most recently.
     */
    readonly activeList: Orderlist | undefined;

    /**
     * All order lists that are currently stored within the state.
     * The order lists are sorted by modification date.
     * The most recently modified list comes first.
     */
    readonly orderlists: ReadonlyArray<Orderlist>;

    /**
     * The order list that is currently selected.
     *
     * @see AddToCartOrListState
     * @see TargetSelectionListDropdown
     */
    readonly selectedOrderList: Orderlist | undefined;

    /**
     * Creates a new order list with the given name.
     */
    readonly createNewList: (listName: string) => void;
};

/**
 * Provides access to the orderlists which are currently stored within the state.
 */
export function useOrderLists(): UseOrderLists {
    const dispatch = useDispatch<AppDispatch>();
    const orderlists = useSelector(selectOrderLists);
    const selectedOrderList = useSelector(selectSelectedOrderList);
    const activeList = orderlists[0];

    const createNewList = useCallback((listName: string) => dispatch(createSelectionlist(listName)), [dispatch]);

    return { orderlists, activeList, selectedOrderList, createNewList };
}

function selectOrderLists(state: AppState): OrderListsState['list'] {
    return state.orderlists.list;
}

function selectSelectedOrderList(state: AppState): Orderlist | undefined {
    const orderlists = state.orderlists.list;
    const selectedOrderlistId = state.addToCartOrList.targetSelectionListId;

    if (!orderlists || orderlists.length < 1) {
        return undefined;
    }

    if (!selectedOrderlistId) {
        return orderlists[0];
    }

    const selectedOrderlist = find(orderlists, orderlist => orderlist.id === selectedOrderlistId);

    if (!selectedOrderlist) {
        return orderlists[0];
    }

    return selectedOrderlist;
}
